/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Input } from '@grupoboticario/flora-react-input'
import { Box } from '@grupoboticario/flora-react-box'
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu'
import { useDropdown, DropdownProps } from './libs/useDropdown'
import { SelectNativeInput, StyledDropdownContent, DropdownIcon } from './DropdownComponents'
import { forwardRef, styled } from '@grupoboticario/flora-react-system'

function isEmpty(display: string) {
  return !display || !String(display).trim()
}

const ContentDropdown = styled('div', {
  overflow: 'auto',
  '& [data-flora="input-wrapper"]': {
    overflow: 'hidden',
  },
})

export const DropdownInput = forwardRef<'input', DropdownProps>(
  (
    {
      name,
      multiple,
      children,
      disabled,
      value: valueProp,
      defaultValue,
      renderValue,
      onChange,
      readOnly,
      native, // eslint-disable-line @typescript-eslint/no-unused-vars
      id,
      required,
      placeholder,
      labelText,
      leftIcon,
      rightIcon,
      invalid,
      invalidText,
      optionalText,
      onOpenChange,
      variant,
      validText,
      'data-valid': dataValid,
      css,
      disableNativeOnMobile,
      ...props
    },
    forwardedRef,
  ) => {
    const {
      items,
      display,
      value,
      inputSelectRef,
      handleChange,
      filled,
      update,
      isOpen,
      handleMouseDown,
    } = useDropdown<HTMLInputElement>({
      multiple,
      children,
      valueProp,
      defaultValue,
      onChange,
      onOpenChange,
      renderValue,
      variant,
      readOnly,
    })

    const [hasClickEventListener, setHasClickEventListener] = useState(false)
    const wrapperRef = useRef<HTMLDivElement>(null)
    const contentRef = useRef<HTMLDivElement>(null)
    let sizeWidth: Number = 0

    const handleClickEvent = useCallback(
      (e: { target: any }) => {
        if (!wrapperRef.current && !contentRef.current) {
          return
        }
        const isOutsideClick =
          !wrapperRef.current?.contains(e.target) && !contentRef.current?.contains(e.target)
        isOutsideClick && update(false)
      },
      [update],
    )

    const addClickEventListener = useCallback(() => {
      document.addEventListener('click', handleClickEvent)
      document.addEventListener('touchstart', handleClickEvent)
      setHasClickEventListener(true)
    }, [handleClickEvent])

    const removeClickEventListener = useCallback(() => {
      document.removeEventListener('click', handleClickEvent)
      document.removeEventListener('touchstart', handleClickEvent)
      setHasClickEventListener(false)
    }, [handleClickEvent])

    const verifySizeWidth = useCallback(() => {
      const widthElement = wrapperRef.current?.clientWidth
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      if (widthElement) return (sizeWidth = widthElement)
    }, [])

    useEffect(() => {
      isOpen && !hasClickEventListener && addClickEventListener()
      !isOpen && hasClickEventListener && removeClickEventListener()
      verifySizeWidth()
    }, [
      verifySizeWidth,
      handleClickEvent,
      hasClickEventListener,
      isOpen,
      addClickEventListener,
      removeClickEventListener,
    ])

    return (
      <Box data-flora="dropdown-wrapper" css={css} ref={wrapperRef}>
        <DropdownMenuPrimitive.Root open={isOpen}>
          <DropdownMenuPrimitive.Trigger asChild>
            <ContentDropdown>
              <Input
                {...{
                  id,
                  required,
                  placeholder,
                  labelText,
                  leftIcon,
                  rightIcon,
                  disabled,
                  invalid,
                  invalidText,
                  'data-valid': dataValid,
                  validText: validText,
                  optionalText,
                }}
                ref={inputSelectRef}
                as="div"
                css={{
                  textAlign: 'left',
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  pointerEvents: 'visible',
                }}
                role="button"
                tabIndex={disabled ? -1 : 0}
                aria-disabled={disabled as boolean}
                aria-expanded={isOpen ? 'true' : 'false'}
                aria-haspopup="listbox"
                onClick={disabled || readOnly ? () => {} : handleMouseDown}
                filled={filled}
                focused={!disabled && isOpen}
                rightIcon={<DropdownIcon open={isOpen} />}
                variant={variant}
                onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => {
                  if (['Enter', ' '].includes(event.key)) {
                    event.preventDefault()
                    return disabled || readOnly ? () => {} : update(!isOpen)
                  }
                }}
                data-flora={multiple ? 'multiple-select' : 'select'}
              >
                {isEmpty(display) ? null : display}
              </Input>
            </ContentDropdown>
          </DropdownMenuPrimitive.Trigger>
          <SelectNativeInput
            ref={forwardedRef}
            value={Array.isArray(value) ? value.join(',') : value}
            name={name}
            aria-hidden
            tabIndex={-1}
            onChange={handleChange}
            disabled={disabled}
            {...props}
          />

          <StyledDropdownContent
            data-flora={multiple ? 'dropdown-content multiple-select' : 'dropdown-content select'}
            role="listbox"
            ref={contentRef}
            css={{
              width: verifySizeWidth(),
              maxHeight: items?.length > 4 ? 256 : 226,
              overflowY: items?.length > 4 ? 'scroll' : 'auto',
              zIndex: 3000,
            }}
            aria-labelledby={`${id}-label-msg`}
            align="start"
            onEscapeKeyDown={(e) => update(false)}
            variant={variant as any}
          >
            {items}
          </StyledDropdownContent>
        </DropdownMenuPrimitive.Root>
      </Box>
    )
  },
)
